import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import {
  CaretDownIcon,
  CheckCircleIcon,
  CircleIcon,
  ClockIcon,
  FileMagnifyingGlassIcon,
  ShieldIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { MotiView } from "moti";
import { useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import type { NavigationProp, RouteProp } from "@react-navigation/native";
import { useDebounce } from "use-debounce";
import { useForm } from "react-hook-form";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CourseInfoCard from "../../components/adminDashboard/CourseInfoCard/CourseInfoCard";
import { useAppConfig } from "../../AppConfigProvider";
import { hasMinRole, UserRoleUI } from "../../constants/rolesAndPermissions";
import { useToast } from "../../contexts/Toast/ToastProvider";
import { useAuth } from "../../hooks/useAuth";
import useFileUpload, { type ProcessedFile } from "../../hooks/useFileUpload";
import Sentry from "../../utils/sentry";
import {
  AcademicTermSeason,
  CourseModality,
  CourseModernizerIntakeStatus,
  CourseModernizerSourceType,
  CourseType,
  EducationLevel,
  ExternalCourseModernizationStatus,
  GuidelineProcessingStatus,
  InstitutionalGuidelinesBySchoolDocument,
  ListExternalCoursesDocument,
  ModernizeCourseGenerationStatus,
  RubricParseStatus,
  StoragePathType,
  type ActiveRubricSetQuery,
  type ExternalCourse,
  type GetExternalCourseForModernizerQuery,
  type ListExternalCoursesQuery,
  type RubricSetQuery,
  type RubricSetParseStatusQuery,
  type GetCourseModernizerIntakeQuery,
  type UploadExternalCourseDocumentAndModernizeMutation,
  useActiveRubricSetQuery,
  useCreateInstitutionalGuidelineFromFileMutation,
  useCreateRubricSetFromFileMutation,
  useDeleteInstitutionalGuidelineMutation,
  useGetModernizeCourseJobStatusQuery,
  useGetActiveModernizeCourseJobQuery,
  useGetCourseModernizerIntakeLazyQuery,
  useGetExternalCourseForModernizerLazyQuery,
  useGetUsersWithElevatedRolesQuery,
  useInstitutionalGuidelinesBySchoolQuery,
  useListExternalCoursesQuery,
  useRubricSetQuery,
  useRubricSetsBySchoolQuery,
  useRubricSetParseStatusQuery,
  useSetActiveRubricSetMutation,
  useUpdateRubricSetMutation,
  useCancelModernizeExternalCourseMutation,
  useModernizeExternalCourseMutation,
  useResetCourseModernizerIntakeMutation,
  useSaveCourseModernizerSetupMutation,
  useSetExternalCourseTermMutation,
  useStartCourseModernizerIntakeMutation,
  useUploadExternalCourseDocumentAndModernizeMutation,
  useGetModernizedCourseLazyQuery,
} from "@axio/graphql/generated/graphql";

import type { RootStackParamList } from "../../navigation/navigation.types";
import RubricConfigSlideIn from "../../components/adminDashboard/CourseModernizer/RubricConfigSlideIn/RubricConfigSlideIn";
import CourseModernizerCoursesTable, {
  type CourseModernizerCourseRow,
  type CourseModernizerRowActionId,
} from "../../components/adminDashboard/CourseModernizer/CourseModernizerCoursesTable";
import CourseModernizerSetupDrawer, {
  type CourseModernizerSetupFormState,
  type CourseModernizerSetupUserOption,
} from "../../components/adminDashboard/CourseModernizer/CourseModernizerSetupDrawer";
import {
  getCourseTermFilterOptions,
  getCourseTermOptionFromLabel,
  type CourseTermOption,
  type PersistedCourseTerm,
} from "../../components/adminDashboard/CourseModernizer/courseTermOptions";
import { CourseModernizeProgressModal } from "../../components/adminDashboard/CourseModernizer/CourseModernizeProgressModal";
import type { CourseModernizeProgressStep } from "../../components/adminDashboard/CourseModernizer/CourseModernizeProgressModal";
import CourseUploadDocumentModal from "../../components/adminDashboard/CourseModernizer/CourseUploadDocumentModal";
import {
  getCourseRowFromExternalCourse,
  getCourseModernizerSourceHints,
  getCourseTermLabel,
  getTeachingFacultySummary,
  isDirectFacultyCourseModernizerUser,
} from "./courseModernizer/courseModernizerRows";
import {
  buildListExternalCoursesInput,
  canManageCourseModernizerRubric,
  canViewAllCourseModernizerCourses,
  EMPTY_COURSE_SUMMARY,
  EVALUATION_NO_RUBRIC_REASON,
  getActiveFilterForStatusFilters,
  getStatusFiltersForActiveFilter,
  MODERNIZER_STATUS_FILTER_OPTIONS,
  type CourseSourceFilter,
  type ModernizerFilter,
} from "./courseModernizer/courseModernizerListFilters";
import { inferUploadedCourseMetadata } from "./courseModernizer/uploadedCourseMetadata";

type RubricBadgeTone = "warning" | "success" | "processing" | "error";
type CourseModernizerRoute = RouteProp<RootStackParamList, "Course Modernizer">;
type ListExternalCoursesPayload = NonNullable<ListExternalCoursesQuery["listExternalCourses"]>;

interface UploadedRubricFile {
  fileId: string;
  name: string;
  size: number | null;
}

interface UploadedGuidelineFile {
  fileId: string;
  name: string;
  size: number | null;
}

type ActiveRubricViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSetViewModel = NonNullable<RubricSetQuery["rubricSet"]["data"]>;
type ParsingRubricStatusViewModel = NonNullable<RubricSetParseStatusQuery["rubricSet"]["data"]>;

const rubricInProgressStatuses = [RubricParseStatus.Pending, RubricParseStatus.Parsing];

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong");

const getRubricNameFromFileName = (fileName: string) => fileName.replace(/\.[^/.]+$/, "").trim() || fileName;

const getGuidelineNameFromFileName = (fileName: string) =>
  fileName
    .replace(/\.[^/.]+$/, "")
    .replace(/[_-]/g, " ")
    .trim() || fileName;

function PageHeader({
  rubricBadgePrimaryText,
  rubricBadgeSecondaryText,
  rubricBadgeTone,
  canManageRubric,
  onRubricBadgePress,
  onUploadDocumentPress,
}: {
  rubricBadgePrimaryText: string;
  rubricBadgeSecondaryText: string | null;
  rubricBadgeTone: RubricBadgeTone;
  canManageRubric: boolean;
  onRubricBadgePress?: () => void;
  onUploadDocumentPress: () => void;
}) {
  const isConfigured = rubricBadgeTone === "success";
  const badgeIconColor = isConfigured ? "#3E68FE" : rubricBadgeTone === "error" ? "#B42318" : "#B54708";
  const rubricBadgeToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricBadge
      : rubricBadgeTone === "error"
        ? styles.errorRubricBadge
        : rubricBadgeTone === "processing"
          ? styles.processingRubricBadge
          : styles.warningRubricBadge;
  const rubricPrimaryTextToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricPrimaryText
      : rubricBadgeTone === "error"
        ? styles.errorRubricText
        : rubricBadgeTone === "processing"
          ? styles.processingRubricText
          : styles.warningRubricText;
  const rubricSecondaryTextToneStyle =
    rubricBadgeTone === "success"
      ? styles.configuredRubricSecondaryText
      : rubricBadgeTone === "error"
        ? styles.errorRubricText
        : rubricBadgeTone === "processing"
          ? styles.processingRubricText
          : styles.warningRubricText;

  const badgeContent = (
    <>
      <ShieldIcon color={badgeIconColor} size={14} weight="bold" />
      <CustomText
        text={rubricBadgePrimaryText}
        size="s"
        weight="medium"
        style={[styles.rubricBadgePrimaryText, rubricPrimaryTextToneStyle]}
        useTranslationText={false}
      />
      {rubricBadgeSecondaryText ? (
        <CustomText
          text={rubricBadgeSecondaryText}
          size="xs"
          weight="medium"
          style={[styles.rubricBadgeSecondaryText, rubricSecondaryTextToneStyle]}
          useTranslationText={false}
        />
      ) : null}
      {canManageRubric && isConfigured ? <CaretDownIcon color="#84ADFF" size={12} weight="bold" /> : null}
    </>
  );
  const rubricBadgeAccessibilityLabel = rubricBadgeSecondaryText
    ? `${rubricBadgePrimaryText}. ${rubricBadgeSecondaryText}`
    : rubricBadgePrimaryText;

  return (
    <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }} style={styles.headerCard}>
      <View style={styles.headerRow}>
        <CustomText
          text="Course Modernizer"
          size="m"
          textType="display"
          weight="ultraBold"
          style={styles.headerTitle}
          role="heading"
          aria-level="1"
          useTranslationText={false}
        />
        {canManageRubric ? (
          <CustomButton
            text="Upload Document"
            styleType="primaryLight"
            size="s"
            bold={true}
            leftIcon={<UploadSimpleIcon size={14} color="#475467" />}
            rightIcon={null}
            style={styles.uploadDocumentButton}
            textStyle={styles.uploadDocumentButtonText}
            useTranslationText={false}
            onPress={onUploadDocumentPress}
          />
        ) : null}
      </View>
      <CustomSpacing type="vertical" size="s" />
      {canManageRubric ? (
        <Pressable
          role="button"
          accessibilityLabel={rubricBadgeAccessibilityLabel}
          onPress={onRubricBadgePress}
          style={[styles.rubricBadge, rubricBadgeToneStyle]}>
          {badgeContent}
        </Pressable>
      ) : (
        <View
          accessible
          accessibilityRole="text"
          accessibilityLabel={rubricBadgeAccessibilityLabel}
          style={[styles.rubricBadge, rubricBadgeToneStyle]}>
          {badgeContent}
        </View>
      )}
    </MotiView>
  );
}

const toAcademicTermSeason = (season: CourseTermOption["season"]): AcademicTermSeason => {
  switch (season) {
    case "Spring":
      return AcademicTermSeason.Spring;
    case "Fall":
      return AcademicTermSeason.Fall;
    default: {
      const exhaustiveSeason: never = season;
      throw new Error(`Unsupported academic term season: ${exhaustiveSeason}`);
    }
  }
};

type CourseModernizerSetupIntake = NonNullable<
  NonNullable<GetCourseModernizerIntakeQuery["getCourseModernizerIntake"]>["data"]
>;

type CourseModernizerSetupUser = {
  id?: string | null;
  name?: string | null;
  email?: string | null;
};
type SetupUserSearchField = "architect" | "editingTeam";

const DEFAULT_SETUP_FORM: CourseModernizerSetupFormState = {
  intakeId: "",
  courseType: CourseType.LiveSection,
  name: "",
  courseCode: "",
  architectId: "",
  editingTeam: [],
  term: "",
  educationLevel: "",
  yearLevel: "",
  credits: "",
  lengthInWeeks: "",
  modality: CourseModality.Hybrid,
};

const getModernizerCourseCode = (course: ExternalCourse): string =>
  (course.courseCode || course.providerCourseId || "").trim();

type DefaultSetupArchitectOptions = {
  externalCourse: ExternalCourse;
  intake: CourseModernizerSetupIntake | null;
  currentUserId?: string | null;
  lmsDirectFacultyMode?: boolean;
  userRole: UserRoleUI;
};

export const getDefaultSetupArchitectId = ({
  externalCourse,
  intake,
  currentUserId,
  lmsDirectFacultyMode,
  userRole,
}: DefaultSetupArchitectOptions): string => {
  if (intake?.architect?.id) {
    return intake.architect.id;
  }

  const isDirectFacultyForCourse = isDirectFacultyCourseModernizerUser({
    course: externalCourse,
    currentUserId,
    lmsDirectFacultyMode,
    userRole,
  });

  if (isDirectFacultyForCourse && currentUserId) {
    return currentUserId;
  }

  return "";
};

const toSetupUserOption = (user: CourseModernizerSetupUser): CourseModernizerSetupUserOption | null => {
  if (!user.id) {
    return null;
  }

  return {
    id: user.id,
    label: user.name || user.email || "Unnamed user",
    subtitle: user.email ?? null,
  };
};

const addSetupUserOption = (options: Map<string, CourseModernizerSetupUserOption>, user: CourseModernizerSetupUser) => {
  const option = toSetupUserOption(user);
  if (option) {
    options.set(option.id, option);
  }
};

export const getPinnedSetupUserOptions = (
  intake: CourseModernizerSetupIntake | null
): CourseModernizerSetupUserOption[] => {
  const options = new Map<string, CourseModernizerSetupUserOption>();

  if (intake?.architect) {
    addSetupUserOption(options, intake.architect);
  }

  intake?.editingTeamConnection.edges.forEach((edge) => {
    addSetupUserOption(options, edge.node);
  });

  return Array.from(options.values());
};

const isPositiveFiniteNumber = (value: string): boolean => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
};

export const getSetupValidationMessage = (
  setupForm: CourseModernizerSetupFormState,
  activeRubric: ActiveRubricViewModel | null
): string | null => {
  if (!activeRubric?.id || activeRubric.parseStatus !== RubricParseStatus.Ready) {
    return "A ready active quality framework is required before setup can be saved.";
  }
  if (!setupForm.name.trim()) {
    return "Course name is required.";
  }
  if (!setupForm.courseCode.trim()) {
    return "Course code is required.";
  }
  if (!setupForm.architectId) {
    return "Select a course architect.";
  }
  if (!setupForm.term.trim()) {
    return "Term is required.";
  }
  if (!setupForm.educationLevel) {
    return "Education level is required.";
  }
  if (!setupForm.yearLevel.trim()) {
    return "Year level is required.";
  }
  if (!setupForm.credits.trim()) {
    return "Credits are required.";
  }
  if (!isPositiveFiniteNumber(setupForm.credits)) {
    return "Credits must be positive.";
  }
  if (!setupForm.lengthInWeeks.trim()) {
    return "Course length is required.";
  }
  if (!isPositiveFiniteNumber(setupForm.lengthInWeeks)) {
    return "Course length must be positive.";
  }
  return null;
};

export const getUploadedCourseShellSettingsCourseId = (
  payload:
    | UploadExternalCourseDocumentAndModernizeMutation["uploadExternalCourseDocumentAndModernize"]
    | null
    | undefined
): string | null => {
  if (!payload?.success || payload.requiresIntakeWizard !== false) {
    return null;
  }

  if (!payload.externalCourseId || !payload.modernizerIntakeId) {
    throw new Error("Upload succeeded but shell settings details were missing");
  }

  return payload.externalCourseId;
};

export const getUploadedCourseShellSettingsCourse = async (
  externalCourseId: string,
  fetchExternalCourseForModernizer: (
    externalCourseId: string
  ) => Promise<GetExternalCourseForModernizerQuery["getExternalCourseForModernizer"]>
): Promise<ExternalCourse> => {
  const payload = await fetchExternalCourseForModernizer(externalCourseId);
  const course = payload?.data ?? null;

  if (!payload?.success || !course) {
    throw new Error(payload?.message || "Failed to load uploaded course shell settings");
  }

  return course as ExternalCourse;
};

export const buildSetupFormFromShell = (
  externalCourse: ExternalCourse,
  intake: CourseModernizerSetupIntake | null,
  selectedTerm: string | null,
  defaultArchitectId: string
): CourseModernizerSetupFormState => {
  const sourceHints = getCourseModernizerSourceHints(externalCourse);

  return {
    intakeId: intake?.id ?? "",
    courseType: intake?.courseType ?? CourseType.LiveSection,
    name: intake?.name?.trim() || externalCourse.name || "",
    courseCode: intake?.courseCode?.trim() || getModernizerCourseCode(externalCourse),
    architectId: defaultArchitectId,
    editingTeam:
      intake?.editingTeamConnection.edges.map((edge) => ({
        userId: edge.node.id,
        role: edge.properties.role,
      })) ?? [],
    term: intake?.term?.trim() || selectedTerm || sourceHints.termLabel || "",
    educationLevel: intake?.educationLevel ?? "",
    yearLevel: intake?.yearLevel ?? "",
    credits: intake?.credits != null ? `${intake.credits}` : "",
    lengthInWeeks:
      intake?.lengthInWeeks != null
        ? `${intake.lengthInWeeks}`
        : sourceHints.lengthInWeeks != null
          ? `${sourceHints.lengthInWeeks}`
          : "",
    modality: intake?.modality ?? CourseModality.Hybrid,
  };
};

function CourseModernizerView() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<CourseModernizerRoute>();
  const isFocused = useIsFocused();
  const { userInfo } = useAuth();
  const appConfig = useAppConfig();
  const showToast = useToast()?.showToast ?? (() => ({ id: "", close: () => {} }));
  const userRole = userInfo.userRole ?? UserRoleUI.STUDENT;
  const isAdmin = canViewAllCourseModernizerCourses(userRole);
  const canManageRubric = canManageCourseModernizerRubric(userRole);
  const [rubricSheetOpen, setRubricSheetOpen] = useState(false);
  const [selectedRubricFile, setSelectedRubricFile] = useState<UploadedRubricFile | null>(null);
  const [rubricUploadInProgress, setRubricUploadInProgress] = useState(false);
  const [rubricCreateInProgress, setRubricCreateInProgress] = useState(false);
  const [rubricUploadError, setRubricUploadError] = useState<string | null>(null);
  const [selectedGuidelineFile, setSelectedGuidelineFile] = useState<UploadedGuidelineFile | null>(null);
  const [guidelineUploadInProgress, setGuidelineUploadInProgress] = useState(false);
  const [guidelineCreateInProgress, setGuidelineCreateInProgress] = useState(false);
  const [guidelineUploadError, setGuidelineUploadError] = useState<string | null>(null);
  const [deletingGuidelineId, setDeletingGuidelineId] = useState<string | null>(null);
  const [reportedFailedGuidelineIds, setReportedFailedGuidelineIds] = useState<Set<string>>(() => new Set());
  const [parsingRubricId, setParsingRubricId] = useState<string | null>(null);
  const [completedParsingRubricId, setCompletedParsingRubricId] = useState<string | null>(null);
  const [activatingRubricId, setActivatingRubricId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ModernizerFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [facultyFilter, setFacultyFilter] = useState("all");
  const [facultyFilterDropdownOpen, setFacultyFilterDropdownOpen] = useState(false);
  const [termFilter, setTermFilter] = useState("all");
  const [termFilterDropdownOpen, setTermFilterDropdownOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<CourseSourceFilter>("all");
  const [sourceFilterDropdownOpen, setSourceFilterDropdownOpen] = useState(false);
  const [statusFilters, setStatusFilters] = useState<ExternalCourseModernizationStatus[]>([]);
  const [courseTermsByCourseId, setCourseTermsByCourseId] = useState<Partial<Record<string, string | null>>>({});
  const [courseTermDropdownCourseId, setCourseTermDropdownCourseId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);
  const [selectedCourseDocument, setSelectedCourseDocument] = useState<ProcessedFile | null>(null);
  const [uploadedCourseName, setUploadedCourseName] = useState("");
  const [uploadedCourseCode, setUploadedCourseCode] = useState("");
  const [uploadedCourseTerm, setUploadedCourseTerm] = useState("none");
  const [courseDocumentUploadInProgress, setCourseDocumentUploadInProgress] = useState(false);
  const [courseDocumentSubmitInProgress, setCourseDocumentSubmitInProgress] = useState(false);
  const [courseDocumentUploadError, setCourseDocumentUploadError] = useState<string | null>(null);
  const [isSetupDrawerOpen, setIsSetupDrawerOpen] = useState(false);
  const [selectedCourseForSetup, setSelectedCourseForSetup] = useState<ExternalCourse | null>(null);
  const [setupPinnedUserOptions, setSetupPinnedUserOptions] = useState<CourseModernizerSetupUserOption[]>([]);
  const {
    handleSubmit: handleSubmitSetupForm,
    reset: resetSetupForm,
    watch: watchSetupForm,
  } = useForm<CourseModernizerSetupFormState>({
    defaultValues: DEFAULT_SETUP_FORM,
  });
  const setupForm = watchSetupForm();
  const [setupArchitectSearchQuery, setSetupArchitectSearchQuery] = useState("");
  const [setupEditingTeamSearchQuery, setSetupEditingTeamSearchQuery] = useState("");
  const [setupUserSearchField, setSetupUserSearchField] = useState<SetupUserSearchField>("architect");
  const activeSetupUserSearchQuery =
    setupUserSearchField === "editingTeam" ? setupEditingTeamSearchQuery : setupArchitectSearchQuery;
  const [debouncedSetupUserSearchQuery] = useDebounce(activeSetupUserSearchQuery, 400);
  const [isStartingSetup, setIsStartingSetup] = useState(false);
  const resetSetupUserSearch = useCallback(() => {
    setSetupArchitectSearchQuery("");
    setSetupEditingTeamSearchQuery("");
    setSetupUserSearchField("architect");
  }, []);
  const [evaluationModalVisible, setEvaluationModalVisible] = useState(false);
  const [activeModernizeJobId, setActiveModernizeJobId] = useState<string | null>(null);
  const [activeExternalCourseId, setActiveExternalCourseId] = useState<string | null>(null);
  const [activeExternalCourseName, setActiveExternalCourseName] = useState<string | null>(null);
  const [isStartingEvaluation, setIsStartingEvaluation] = useState(false);
  const [isCancellingEvaluation, setIsCancellingEvaluation] = useState(false);
  const [resettingModernizerIntakeId, setResettingModernizerIntakeId] = useState<string | null>(null);
  const completedCourseRefetchJobIdRef = useRef<string | null>(null);
  const consumedRouteModernizeJobIdRef = useRef<string | null>(null);
  const wasFocusedRef = useRef(isFocused);

  const {
    data: activeRubricData,
    loading: activeRubricLoading,
    refetch: refetchActiveRubric,
  } = useActiveRubricSetQuery({});

  const activeRubric = activeRubricData?.activeRubricSet?.data ?? null;
  const setupSchoolId = appConfig.axioSchools?.[0] ?? "";
  const { data: setupUsersData } = useGetUsersWithElevatedRolesQuery({
    variables: {
      schoolId: setupSchoolId,
      searchQuery: debouncedSetupUserSearchQuery,
      page: 1,
      pageSize: 20,
      sortBy: "name_asc",
    },
    skip: !isSetupDrawerOpen || !setupSchoolId,
  });
  const {
    data: availableRubricsData,
    loading: availableRubricsLoading,
    refetch: refetchRubricSets,
  } = useRubricSetsBySchoolQuery({
    skip: !canManageRubric,
  });
  const availableRubrics = availableRubricsData?.rubricSetsBySchool?.data ?? [];

  const {
    data: guidelinesData,
    loading: guidelinesLoading,
    startPolling: startGuidelinesPolling,
    stopPolling: stopGuidelinesPolling,
  } = useInstitutionalGuidelinesBySchoolQuery({
    fetchPolicy: "cache-and-network",
    skip: !canManageRubric,
  });
  const guidelines = guidelinesData?.institutionalGuidelinesBySchool?.data ?? [];

  const {
    data: parsingRubricData,
    loading: parsingRubricLoading,
    stopPolling: stopParsingRubricPolling,
  } = useRubricSetParseStatusQuery({
    variables: { id: parsingRubricId ?? "" },
    skip: !canManageRubric || !parsingRubricId,
    pollInterval: parsingRubricId ? 2000 : 0,
  });

  const parsingRubricStatus = parsingRubricData?.rubricSet?.data ?? null;
  const parsingRubricIsReady = parsingRubricStatus?.parseStatus === RubricParseStatus.Ready;
  const {
    data: parsedRubricData,
    loading: parsedRubricLoading,
    refetch: refetchParsedRubric,
  } = useRubricSetQuery({
    variables: { id: parsingRubricId ?? "" },
    skip: !canManageRubric || !parsingRubricId || !parsingRubricIsReady,
    fetchPolicy: "network-only",
  });
  const parsedRubric = parsedRubricData?.rubricSet?.data ?? null;
  const waitingForReadyRubric = parsingRubricIsReady && parsingRubricId != null && activeRubric?.id !== parsingRubricId;
  const displayedRubric: ActiveRubricViewModel | RubricSetViewModel | ParsingRubricStatusViewModel | null =
    parsedRubric && parsingRubricIsReady
      ? parsedRubric
      : parsingRubricStatus && (!parsingRubricIsReady || waitingForReadyRubric)
        ? parsingRubricStatus
        : activeRubric;
  const [createRubricSetFromFile] = useCreateRubricSetFromFileMutation();
  const [createInstitutionalGuidelineFromFile] = useCreateInstitutionalGuidelineFromFileMutation();
  const [deleteInstitutionalGuideline] = useDeleteInstitutionalGuidelineMutation();
  const [setActiveRubricSet] = useSetActiveRubricSetMutation();
  const [updateRubricSet, { loading: savingRubric }] = useUpdateRubricSetMutation();
  const [modernizeExternalCourse] = useModernizeExternalCourseMutation();
  const [uploadExternalCourseDocumentAndModernize] = useUploadExternalCourseDocumentAndModernizeMutation();
  const [cancelModernizeExternalCourse] = useCancelModernizeExternalCourseMutation();
  const [resetCourseModernizerIntake] = useResetCourseModernizerIntakeMutation();
  const [setExternalCourseTerm] = useSetExternalCourseTermMutation();
  const [getCourseModernizerIntake] = useGetCourseModernizerIntakeLazyQuery();
  const [getExternalCourseForModernizer] = useGetExternalCourseForModernizerLazyQuery();
  const [saveCourseModernizerSetup, { loading: savingCourseModernizerSetup }] = useSaveCourseModernizerSetupMutation();
  const [fetchModernizedCourse] = useGetModernizedCourseLazyQuery({ fetchPolicy: "network-only" });

  const {
    data: modernizeJobStatusData,
    loading: modernizeJobStatusLoading,
    refetch: refetchModernizeJobStatus,
  } = useGetModernizeCourseJobStatusQuery({
    variables: { jobId: activeModernizeJobId ?? "" },
    skip: !activeModernizeJobId,
    pollInterval: activeModernizeJobId ? 2000 : 0,
    fetchPolicy: "network-only",
  });

  const uploadErrorHandler = (message?: string) => {
    const errorMessage = message || "Rubric upload failed";
    setRubricUploadError(errorMessage);
    showToast({ message: errorMessage, type: "error" });
  };

  const { handleFileUpload, allowedMimeTypes, maxFileSize } = useFileUpload({
    storagePath: StoragePathType.Rubrics,
    onError: uploadErrorHandler,
    multiple: false,
  });

  const {
    handleFileUpload: handleCourseDocumentFileUpload,
    allowedMimeTypes: courseDocumentAllowedMimeTypes,
    maxFileSize: courseDocumentMaxFileSize,
  } = useFileUpload({
    storagePath: StoragePathType.CourseModernizerDocuments,
    allowedMimeTypesOverride: [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ims.imsccv1p3",
      "application/zip",
    ],
    allowedFileExtensions: ["pdf", "docx", "imscc", "zip"],
    onError: (message?: string) => {
      const errorMessage = message || "Course document upload failed";
      setCourseDocumentUploadError(errorMessage);
      showToast({ message: errorMessage, type: "error" });
    },
    multiple: false,
  });

  const guidelineUploadErrorHandler = (message?: string) => {
    const errorMessage = message || "Guideline upload failed";
    setGuidelineUploadError(errorMessage);
    showToast({ message: errorMessage, type: "error" });
  };

  const { handleFileUpload: handleGuidelineFileUpload } = useFileUpload({
    storagePath: StoragePathType.Rubrics,
    onError: guidelineUploadErrorHandler,
    multiple: false,
  });

  useEffect(() => {
    if (rubricSheetOpen && canManageRubric) {
      startGuidelinesPolling(2000);
    } else {
      stopGuidelinesPolling();
    }

    return () => {
      stopGuidelinesPolling();
    };
  }, [canManageRubric, rubricSheetOpen, startGuidelinesPolling, stopGuidelinesPolling]);

  useEffect(() => {
    if (!parsingRubricId || !parsingRubricStatus?.parseStatus) {
      return;
    }

    if (parsingRubricStatus.parseStatus === RubricParseStatus.Ready) {
      stopParsingRubricPolling();
      void Promise.all([refetchActiveRubric(), refetchRubricSets(), refetchParsedRubric()]);
      if (completedParsingRubricId !== parsingRubricId) {
        setCompletedParsingRubricId(parsingRubricId);
        showToast({ message: "Rubric parsed and activated", type: "success" });
      }
      return;
    }

    if (parsingRubricStatus.parseStatus === RubricParseStatus.Failed) {
      stopParsingRubricPolling();
      if (completedParsingRubricId !== parsingRubricId) {
        setCompletedParsingRubricId(parsingRubricId);
        showToast({ message: parsingRubricStatus.parseError || "Rubric parsing failed", type: "error" });
      }
    }
  }, [
    completedParsingRubricId,
    parsingRubricStatus,
    parsingRubricId,
    refetchActiveRubric,
    refetchParsedRubric,
    refetchRubricSets,
    showToast,
    stopParsingRubricPolling,
  ]);

  useEffect(() => {
    if (
      parsingRubricId &&
      activeRubric?.id === parsingRubricId &&
      activeRubric.parseStatus === RubricParseStatus.Ready
    ) {
      setParsingRubricId(null);
    }
  }, [activeRubric, parsingRubricId]);

  useEffect(() => {
    const failedGuidelines = guidelines.filter(
      (guideline) =>
        guideline.processingStatus === GuidelineProcessingStatus.Failed && !reportedFailedGuidelineIds.has(guideline.id)
    );

    if (failedGuidelines.length === 0) {
      return;
    }

    setReportedFailedGuidelineIds((previous) => {
      const next = new Set(previous);
      failedGuidelines.forEach((guideline) => next.add(guideline.id));
      return next;
    });
    failedGuidelines.forEach((guideline) => {
      showToast({
        message: guideline.processingError || `${guideline.name} processing failed`,
        type: "error",
      });
    });
  }, [guidelines, reportedFailedGuidelineIds, showToast]);

  useEffect(() => {
    if (!waitingForReadyRubric) {
      return undefined;
    }

    const interval = setInterval(() => {
      void Promise.all([refetchActiveRubric(), refetchRubricSets()]);
    }, 2000);

    return () => clearInterval(interval);
  }, [refetchActiveRubric, refetchRubricSets, waitingForReadyRubric]);

  const handleSelectRubricFile = async () => {
    setRubricUploadError(null);
    setRubricUploadInProgress(true);
    setParsingRubricId(null);
    setCompletedParsingRubricId(null);

    try {
      const processedFiles = await handleFileUpload();
      const processedFile: ProcessedFile | undefined = processedFiles?.[0];

      if (!processedFile) {
        return;
      }

      setSelectedRubricFile({
        fileId: processedFile.file.id,
        name: processedFile.localUploadedFile.name,
        size: processedFile.localUploadedFile.size ?? processedFile.size ?? null,
      });
    } catch (error) {
      const message = getErrorMessage(error);
      setRubricUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setRubricUploadInProgress(false);
    }
  };

  const handleParseRubric = async () => {
    if (!selectedRubricFile) {
      const message = "Select a rubric file first";
      setRubricUploadError(message);
      showToast({ message, type: "error" });
      return;
    }

    setRubricUploadError(null);
    setRubricCreateInProgress(true);

    try {
      const result = await createRubricSetFromFile({
        variables: {
          input: {
            fileId: selectedRubricFile.fileId,
            name: getRubricNameFromFileName(selectedRubricFile.name),
            setActive: true,
          },
        },
      });

      const response = result.data?.createRubricSetFromFile;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to queue rubric parsing");
      }

      setParsingRubricId(response.data.id);
      setSelectedRubricFile(null);
      void refetchRubricSets();
      showToast({ message: "Rubric parsing started", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      setRubricUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setRubricCreateInProgress(false);
    }
  };

  const handleSelectGuidelineFile = async () => {
    setGuidelineUploadError(null);
    setGuidelineUploadInProgress(true);

    try {
      const processedFiles = await handleGuidelineFileUpload();
      const processedFile: ProcessedFile | undefined = processedFiles?.[0];

      if (!processedFile) {
        return;
      }

      setSelectedGuidelineFile({
        fileId: processedFile.file.id,
        name: processedFile.localUploadedFile.name,
        size: processedFile.localUploadedFile.size ?? processedFile.size ?? null,
      });
    } catch (error) {
      const message = getErrorMessage(error);
      setGuidelineUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setGuidelineUploadInProgress(false);
    }
  };

  const handleCreateGuideline = async (): Promise<boolean> => {
    if (!selectedGuidelineFile) {
      const message = "Select a guideline file first";
      setGuidelineUploadError(message);
      showToast({ message, type: "error" });
      return false;
    }

    if (activeRubric?.parseStatus !== RubricParseStatus.Ready) {
      const message = "Upload and activate a rubric before adding guidelines";
      setGuidelineUploadError(message);
      showToast({ message, type: "error" });
      return false;
    }

    setGuidelineUploadError(null);
    setGuidelineCreateInProgress(true);

    try {
      const result = await createInstitutionalGuidelineFromFile({
        variables: {
          input: {
            fileId: selectedGuidelineFile.fileId,
            name: getGuidelineNameFromFileName(selectedGuidelineFile.name),
          },
        },
        refetchQueries: [{ query: InstitutionalGuidelinesBySchoolDocument }],
        awaitRefetchQueries: true,
      });

      const response = result.data?.createInstitutionalGuidelineFromFile;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to queue guideline processing");
      }

      setSelectedGuidelineFile(null);
      showToast({ message: "Guideline processing started", type: "success" });
      return true;
    } catch (error) {
      const message = getErrorMessage(error);
      setGuidelineUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
      return false;
    } finally {
      setGuidelineCreateInProgress(false);
    }
  };

  const handleDeleteGuideline = async (guidelineId: string) => {
    setDeletingGuidelineId(guidelineId);

    try {
      const result = await deleteInstitutionalGuideline({
        variables: {
          id: guidelineId,
        },
        refetchQueries: [{ query: InstitutionalGuidelinesBySchoolDocument }],
        awaitRefetchQueries: true,
      });
      const response = result.data?.deleteInstitutionalGuideline;
      if (!response?.success) {
        throw new Error(response?.message || "Failed to delete guideline");
      }

      showToast({ message: "Guideline deleted", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setDeletingGuidelineId(null);
    }
  };

  const handleActivateRubric = async (rubricId: string) => {
    setActivatingRubricId(rubricId);
    try {
      const result = await setActiveRubricSet({
        variables: {
          id: rubricId,
        },
      });
      const response = result.data?.setActiveRubricSet;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to activate rubric");
      }

      await Promise.all([refetchActiveRubric(), refetchRubricSets()]);
      showToast({ message: "Rubric activated", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setActivatingRubricId(null);
    }
  };

  const handleUpdateRubric = async ({
    sections,
    criteria,
  }: {
    sections?: Array<{ id: string; title?: string; description?: string | null; resetToOriginal?: boolean }>;
    criteria?: Array<{
      id: string;
      title?: string;
      description?: string;
      sectionId?: string;
      isDeleted?: boolean;
      resetToOriginal?: boolean;
    }>;
  }) => {
    const editableRubric = displayedRubric?.parseStatus === RubricParseStatus.Ready ? displayedRubric : activeRubric;

    if (!editableRubric) {
      showToast({ message: "No active rubric to update", type: "error" });
      return;
    }

    try {
      const result = await updateRubricSet({
        variables: {
          input: {
            id: editableRubric.id,
            sections,
            criteria,
          },
        },
      });
      const response = result.data?.updateRubricSet;
      if (!response?.success || !response.data) {
        throw new Error(response?.message || "Failed to update rubric");
      }

      await Promise.all([refetchActiveRubric(), refetchRubricSets()]);
      showToast({ message: "Rubric updated", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    }
  };

  const variables = useMemo(
    () => ({
      input: buildListExternalCoursesInput({
        activeFilter,
        facultyFilter,
        isAdmin,
        page,
        pageSize,
        searchQuery,
        sourceFilter,
        statusFilters,
        termFilter,
      }),
    }),
    [activeFilter, facultyFilter, isAdmin, page, pageSize, searchQuery, sourceFilter, statusFilters, termFilter]
  );

  const {
    data,
    loading,
    refetch: refetchExternalCourses,
  } = useListExternalCoursesQuery({
    variables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const latestListExternalCoursesPayload = data?.listExternalCourses ?? null;
  const [completedListExternalCoursesPayload, setCompletedListExternalCoursesPayload] =
    useState<ListExternalCoursesPayload | null>(null);

  useEffect(() => {
    if (loading) {
      return;
    }

    setCompletedListExternalCoursesPayload(latestListExternalCoursesPayload);
  }, [latestListExternalCoursesPayload, loading]);

  const listExternalCoursesPayload = loading
    ? (completedListExternalCoursesPayload ?? latestListExternalCoursesPayload)
    : (latestListExternalCoursesPayload ?? completedListExternalCoursesPayload);
  const listExternalCoursesData = listExternalCoursesPayload?.data ?? null;
  const listExternalCoursesInitialLoading = loading && completedListExternalCoursesPayload == null;

  const modernizeStatusPayload = modernizeJobStatusData?.getModernizeCourseJobStatus;
  const generationStatus =
    modernizeStatusPayload?.generationStatus ?? (activeModernizeJobId ? ModernizeCourseGenerationStatus.Running : null);
  const modernizeSteps: CourseModernizeProgressStep[] = (modernizeStatusPayload?.steps ?? []).map((step) => ({
    key: step.key,
    title: step.title,
    agent: step.agent,
    description: step.description,
    status: step.status as CourseModernizeProgressStep["status"],
  }));
  const modernizeCurrentStepIndex = modernizeStatusPayload?.currentStepIndex ?? 0;
  const modernizeTotalSteps = modernizeStatusPayload?.totalSteps ?? modernizeSteps.length;
  const modernizePercent = modernizeStatusPayload?.percent ?? 0;
  const modernizedCourseId = modernizeStatusPayload?.modernizedCourseId ?? null;
  const isGenerationCompleted = generationStatus === ModernizeCourseGenerationStatus.Completed;
  const isGenerationFailed = generationStatus === ModernizeCourseGenerationStatus.Failed;
  const isGenerationTerminal = isGenerationCompleted || isGenerationFailed;
  const normalizedModernizePercent = Math.max(0, Math.min(100, modernizePercent));
  const isEvaluationWorking = Boolean(activeModernizeJobId) && !isGenerationTerminal;

  useEffect(() => {
    if (!activeModernizeJobId || isGenerationTerminal) {
      return;
    }

    void refetchModernizeJobStatus();
  }, [activeModernizeJobId, isGenerationTerminal, refetchModernizeJobStatus]);

  useEffect(() => {
    if (!activeModernizeJobId || !isGenerationCompleted) {
      return;
    }

    if (completedCourseRefetchJobIdRef.current === activeModernizeJobId) {
      return;
    }

    completedCourseRefetchJobIdRef.current = activeModernizeJobId;
    void refetchExternalCourses();
  }, [activeModernizeJobId, isGenerationCompleted, refetchExternalCourses]);

  const { data: activeModernizeCourseJobData, refetch: refetchActiveModernizeCourseJob } =
    useGetActiveModernizeCourseJobQuery({
      fetchPolicy: "network-only",
    });

  const activeModernizeCourseJob = activeModernizeCourseJobData?.getActiveModernizeCourseJob ?? null;

  const cancellationPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (cancellationPollRef.current) {
        clearInterval(cancellationPollRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCancellingEvaluation) {
      if (cancellationPollRef.current) {
        clearInterval(cancellationPollRef.current);
        cancellationPollRef.current = null;
      }
      return;
    }

    if (activeModernizeCourseJob?.generationStatus === ModernizeCourseGenerationStatus.Running) {
      if (!cancellationPollRef.current) {
        cancellationPollRef.current = setInterval(() => {
          void refetchActiveModernizeCourseJob();
        }, 2000);
      }
      return;
    }

    if (cancellationPollRef.current) {
      clearInterval(cancellationPollRef.current);
      cancellationPollRef.current = null;
    }

    setIsCancellingEvaluation(false);
    setActiveModernizeJobId(null);
    setActiveExternalCourseId(null);
    setActiveExternalCourseName(null);
    void refetchExternalCourses();
    showToast({ message: "Evaluation cancelled.", type: "warning" });
  }, [
    activeModernizeCourseJob?.generationStatus,
    isCancellingEvaluation,
    refetchActiveModernizeCourseJob,
    refetchExternalCourses,
    showToast,
  ]);

  const backgroundTerminalHandledJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!activeModernizeJobId || !isGenerationTerminal || evaluationModalVisible || isCancellingEvaluation) {
      return;
    }

    if (backgroundTerminalHandledJobIdRef.current === activeModernizeJobId) {
      return;
    }

    backgroundTerminalHandledJobIdRef.current = activeModernizeJobId;

    if (isGenerationCompleted) {
      showToast({ message: "Course evaluation completed", type: "success" });
    } else {
      showToast({ message: "Course evaluation failed", type: "error" });
    }

    setActiveModernizeJobId(null);
    setActiveExternalCourseId(null);
    setActiveExternalCourseName(null);
    void refetchExternalCourses();
    void refetchActiveModernizeCourseJob();
  }, [
    activeModernizeJobId,
    evaluationModalVisible,
    isCancellingEvaluation,
    isGenerationCompleted,
    isGenerationTerminal,
    refetchActiveModernizeCourseJob,
    refetchExternalCourses,
    showToast,
  ]);

  useEffect(() => {
    const wasFocused = wasFocusedRef.current;
    wasFocusedRef.current = isFocused;

    if (wasFocused || !isFocused) {
      return;
    }

    void refetchExternalCourses();
  }, [isFocused, refetchExternalCourses]);

  const handleStartEvaluation = async (row: CourseModernizerCourseRow) => {
    const externalCourseId = row.course.id;
    const modernizerIntakeId = row.modernizerIntakeId;
    if (!externalCourseId || isStartingEvaluation) {
      return;
    }
    if (isCancellingEvaluation) {
      showToast({ message: "Previous evaluation is still being cancelled. Please wait.", type: "warning" });
      return;
    }
    if (!modernizerIntakeId) {
      showToast({ message: "Complete the modernization intake before starting evaluation.", type: "warning" });
      return;
    }
    if (evaluationDisabledReason) {
      showToast({ message: evaluationDisabledReason, type: "warning" });
      return;
    }
    if (
      courseWithActiveModernization &&
      hasActiveModernizationJob &&
      courseWithActiveModernization.id !== externalCourseId
    ) {
      showToast({
        message: "Another course modernization is already in progress. Please wait until it finishes.",
        type: "warning",
      });
      return;
    }

    setIsStartingEvaluation(true);
    try {
      const termForEvaluation = getCourseTermLabel(row.course, courseTermsByCourseId[externalCourseId]);
      const persistedTermLabel = row.course.term?.label ?? null;
      const hasTermOverride = courseTermsByCourseId[externalCourseId] !== undefined;

      if ((hasTermOverride || termForEvaluation) && termForEvaluation !== persistedTermLabel) {
        const savedTermLabel = await persistExternalCourseTerm(externalCourseId, termForEvaluation);
        setCourseTermsByCourseId((previousTerms) => ({
          ...previousTerms,
          [externalCourseId]: savedTermLabel,
        }));
      }

      const result = await modernizeExternalCourse({
        variables: {
          input: {
            externalCourseId,
          },
        },
      });
      const payload = result.data?.modernizeExternalCourse;

      if (!payload?.success || !payload.jobId) {
        throw new Error(payload?.message || "Failed to start course evaluation");
      }

      setRubricSheetOpen(false);
      setActiveExternalCourseId(externalCourseId);
      setActiveExternalCourseName(row.course.name || "course");
      setActiveModernizeJobId(payload.jobId);
      setEvaluationModalVisible(true);
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setIsStartingEvaluation(false);
    }
  };

  const handleSelectCourseDocument = async () => {
    setCourseDocumentUploadError(null);
    setCourseDocumentUploadInProgress(true);
    try {
      const processedFiles = await handleCourseDocumentFileUpload();
      const processedFile = processedFiles?.[0];
      if (!processedFile) {
        return;
      }

      setSelectedCourseDocument(processedFile);
      const inferredMetadata = await inferUploadedCourseMetadata(processedFile.localUploadedFile);
      setUploadedCourseName(inferredMetadata.courseName ?? "");
      setUploadedCourseCode(inferredMetadata.courseCode ?? "");
    } catch (error) {
      const message = getErrorMessage(error);
      setCourseDocumentUploadError(message);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setCourseDocumentUploadInProgress(false);
    }
  };

  const handleOpenUploadDocumentModal = () => {
    setCourseDocumentUploadError(null);
    setUploadDocumentModalOpen(true);
  };

  const handleCloseUploadDocumentModal = () => {
    setCourseDocumentUploadError(null);
    setUploadDocumentModalOpen(false);
  };

  const handleUploadDocumentEvaluate = async () => {
    if (courseDocumentSubmitInProgress || hasActiveModernizationJob) {
      return;
    }

    if (!selectedCourseDocument?.file?.id) {
      setCourseDocumentUploadError("Select a document before evaluating");
      return;
    }

    if (!activeRubric || activeRubric.parseStatus !== RubricParseStatus.Ready) {
      setCourseDocumentUploadError("An active rubric is required before you can evaluate an uploaded document.");
      return;
    }

    if (!uploadedCourseName.trim()) {
      setCourseDocumentUploadError("Course name is required");
      return;
    }

    const selectedUploadTermOption =
      uploadedCourseTerm === "none"
        ? null
        : (termOptions.find((option) => option.value === uploadedCourseTerm) ??
          getCourseTermOptionFromLabel(uploadedCourseTerm));
    if (uploadedCourseTerm !== "none" && !selectedUploadTermOption) {
      setCourseDocumentUploadError("Selected term is no longer available");
      return;
    }

    setCourseDocumentUploadError(null);
    setCourseDocumentSubmitInProgress(true);
    try {
      const result = await uploadExternalCourseDocumentAndModernize({
        variables: {
          input: {
            fileId: selectedCourseDocument.file.id,
            courseName: uploadedCourseName.trim(),
            courseCode: uploadedCourseCode.trim() || null,
            term: selectedUploadTermOption
              ? {
                  season: toAcademicTermSeason(selectedUploadTermOption.season),
                  year: selectedUploadTermOption.year,
                }
              : null,
          },
        },
        refetchQueries: [{ query: ListExternalCoursesDocument, variables }],
      });

      const payload = result.data?.uploadExternalCourseDocumentAndModernize;
      if (!payload?.success) {
        throw new Error(payload?.message || "Failed to prepare uploaded course setup");
      }
      const shellSettingsCourseId = getUploadedCourseShellSettingsCourseId(payload);
      if (!shellSettingsCourseId) {
        throw new Error(payload.message || "Failed to prepare uploaded course setup");
      }

      setUploadDocumentModalOpen(false);
      setSelectedCourseDocument(null);
      setUploadedCourseCode("");
      setUploadedCourseName("");
      setUploadedCourseTerm("none");
      setActiveExternalCourseId(null);
      setActiveModernizeJobId(null);
      setEvaluationModalVisible(false);
      if (shellSettingsCourseId) {
        showToast({ message: "Upload received. Configure shell settings to start modernization.", type: "success" });
        const uploadedCourse = await getUploadedCourseShellSettingsCourse(
          shellSettingsCourseId,
          async (externalCourseId) => {
            const courseResult = await getExternalCourseForModernizer({
              variables: { externalCourseId },
              fetchPolicy: "network-only",
            });
            return courseResult.data?.getExternalCourseForModernizer;
          }
        );
        await openModernizerSetupDrawer(uploadedCourse);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      setCourseDocumentUploadError(message);
      showToast({ message, type: "error" });
    } finally {
      setCourseDocumentSubmitInProgress(false);
    }
  };

  const handleCancelEvaluation = async () => {
    if (!activeModernizeJobId || isCancellingEvaluation) {
      return;
    }

    setIsCancellingEvaluation(true);
    try {
      const result = await cancelModernizeExternalCourse({
        variables: {
          jobId: activeModernizeJobId,
        },
      });

      const payload = result.data?.cancelModernizeExternalCourse;

      if (!payload?.success) {
        throw new Error(payload?.message || "Failed to cancel evaluation");
      }

      setEvaluationModalVisible(false);
      setActiveModernizeJobId(null);
      setActiveExternalCourseId(null);
      void refetchExternalCourses().catch((refetchError) => {
        Sentry.captureException(refetchError);
      });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setIsCancellingEvaluation(false);
    }
  };

  const handleCloseEvaluationModal = () => {
    if (isCancellingEvaluation) {
      return;
    }

    if (isGenerationTerminal) {
      setEvaluationModalVisible(false);
      setActiveModernizeJobId(null);
      setActiveExternalCourseId(null);
      setActiveExternalCourseName(null);
      void refetchExternalCourses();
      return;
    }

    // Just close the modal — keep polling and the job running in background
    setEvaluationModalVisible(false);
  };

  const handleContinueToResults = () => {
    if (!isGenerationCompleted) {
      return;
    }

    if (!modernizedCourseId) {
      showToast({ message: "Evaluation finished but no review course id was returned", type: "error" });
      return;
    }

    setEvaluationModalVisible(false);
    setActiveModernizeJobId(null);
    setActiveExternalCourseId(null);
    setActiveExternalCourseName(null);
    void refetchExternalCourses();
    navigation.navigate("Course Modernizer Review", { courseId: modernizedCourseId });
  };

  const courses = (listExternalCoursesData?.courses ?? []).filter((course): course is ExternalCourse =>
    Boolean(course)
  );
  const externalCoursesMessage = loading ? "" : (listExternalCoursesPayload?.message?.trim() ?? "");

  const routeActiveModernizeJobId = route.params?.activeModernizeJobId ?? null;
  const routeActiveExternalCourseId = route.params?.activeExternalCourseId ?? null;

  useEffect(() => {
    if (
      !isFocused ||
      !routeActiveModernizeJobId ||
      consumedRouteModernizeJobIdRef.current === routeActiveModernizeJobId
    ) {
      return;
    }

    consumedRouteModernizeJobIdRef.current = routeActiveModernizeJobId;
    setActiveExternalCourseId(routeActiveExternalCourseId);
    setActiveModernizeJobId(routeActiveModernizeJobId);
    setEvaluationModalVisible(true);
    navigation.setParams({
      activeExternalCourseId: undefined,
      activeModernizeJobId: undefined,
    });
  }, [isFocused, navigation, routeActiveExternalCourseId, routeActiveModernizeJobId]);

  useEffect(() => {
    if (!isFocused || activeModernizeJobId || !activeModernizeCourseJob?.jobId) {
      return;
    }

    if (activeModernizeCourseJob.generationStatus !== ModernizeCourseGenerationStatus.Running) {
      return;
    }

    if (backgroundTerminalHandledJobIdRef.current === activeModernizeCourseJob.jobId) {
      return;
    }

    setActiveExternalCourseId(activeModernizeCourseJob.externalCourseId);
    setActiveModernizeJobId(activeModernizeCourseJob.jobId);
    const resumedCourse = courses.find((c) => c.id === activeModernizeCourseJob.externalCourseId);
    if (resumedCourse?.name) {
      setActiveExternalCourseName(resumedCourse.name);
    }
  }, [activeModernizeCourseJob, activeModernizeJobId, courses, isFocused]);

  const termOptions = useMemo(() => {
    const persistedTerms: PersistedCourseTerm[] = [];

    (listExternalCoursesPayload?.data?.filterOptions?.terms ?? []).forEach((option) => {
      if (option.label) {
        persistedTerms.push({ label: option.label });
      }
    });

    courses.forEach((course) => {
      if (course.term?.label) {
        persistedTerms.push({
          label: course.term.label,
          season: course.term.season,
          year: course.term.year,
        });
      }

      const sourceHints = getCourseModernizerSourceHints(course);
      if (sourceHints.termLabel) {
        persistedTerms.push({ label: sourceHints.termLabel });
      }

      const termOverride = courseTermsByCourseId[course.id];
      if (termOverride) {
        persistedTerms.push({ label: termOverride });
      }
    });

    return getCourseTermFilterOptions(persistedTerms, new Date(), { includeSelectableWindow: true });
  }, [courseTermsByCourseId, courses, listExternalCoursesPayload?.data?.filterOptions?.terms]);

  const courseRows = useMemo(
    () =>
      courses.map((course) =>
        getCourseRowFromExternalCourse(course, courseTermsByCourseId[course.id], {
          currentUserId: userInfo.userId,
          lmsDirectFacultyMode: appConfig.lmsDirectFacultyMode,
          userRole,
        })
      ),
    [appConfig.lmsDirectFacultyMode, courseTermsByCourseId, courses, userInfo.userId, userRole]
  );

  const summary = listExternalCoursesData?.summary ?? EMPTY_COURSE_SUMMARY;
  const filterOptions = listExternalCoursesPayload?.data?.filterOptions ?? null;
  const visibleCourses = courseRows;

  const facultyOptions = useMemo(() => {
    return [
      { label: "All Faculty", value: "all" },
      ...(filterOptions?.faculty ?? []).map((option) => ({ label: option.label, value: option.value })),
    ];
  }, [filterOptions?.faculty]);

  const courseWithActiveModernization = useMemo(() => {
    if (activeExternalCourseId) {
      const activeCourse = courses.find((course) => course.id === activeExternalCourseId) ?? null;
      if (activeCourse) {
        return activeCourse;
      }
    }

    const activeJobExternalCourseId = activeModernizeCourseJob?.externalCourseId;
    if (!activeJobExternalCourseId) {
      return null;
    }

    return courses.find((course) => course.id === activeJobExternalCourseId) ?? null;
  }, [activeExternalCourseId, activeModernizeCourseJob?.externalCourseId, courses]);

  const hasActiveModernizationJob = Boolean(
    (activeModernizeCourseJob?.jobId && backgroundTerminalHandledJobIdRef.current !== activeModernizeCourseJob.jobId) ||
      (activeModernizeJobId && !isGenerationTerminal)
  );

  const showActiveModernizationBanner = hasActiveModernizationJob || isCancellingEvaluation;
  const rubricStatus = displayedRubric?.parseStatus;
  const isRubricProcessing = rubricStatus != null && rubricInProgressStatuses.includes(rubricStatus);
  const evaluationDisabledReason =
    activeRubricLoading || (parsingRubricLoading && displayedRubric == null)
      ? "Checking rubric configuration..."
      : rubricStatus === RubricParseStatus.Ready
        ? null
        : EVALUATION_NO_RUBRIC_REASON;
  const hasActiveRubric = activeRubric?.parseStatus === RubricParseStatus.Ready;
  const uploadDocumentCtaDisabledReason = !hasActiveRubric
    ? "Set an active rubric before uploading and evaluating."
    : hasActiveModernizationJob
      ? "Another course modernization is in progress. Wait until it finishes."
      : courseDocumentUploadError;
  const uploadDocumentCtaDisabled =
    !hasActiveRubric ||
    hasActiveModernizationJob ||
    !selectedCourseDocument?.file?.id ||
    !uploadedCourseName.trim() ||
    courseDocumentUploadInProgress ||
    courseDocumentSubmitInProgress;

  const totalItems = listExternalCoursesData?.totalCount ?? 0;

  const termItems = useMemo(() => {
    const optionsByValue = new Map<string, { label: string; value: string }>();

    (filterOptions?.terms ?? []).forEach((option) => {
      if (!option.value || !option.label) {
        return;
      }

      optionsByValue.set(option.value, { label: option.label, value: option.value });
    });

    courseRows.forEach((row) => {
      const label = row.termLabel?.trim();
      if (!label) {
        return;
      }

      optionsByValue.set(label, { label, value: label });
    });

    const sortedItems = Array.from(optionsByValue.values()).sort((left, right) => {
      const leftTerm = getCourseTermOptionFromLabel(left.value);
      const rightTerm = getCourseTermOptionFromLabel(right.value);

      if (leftTerm && rightTerm) {
        if (leftTerm.year !== rightTerm.year) {
          return leftTerm.year - rightTerm.year;
        }

        if (leftTerm.season !== rightTerm.season) {
          return leftTerm.season === "Spring" ? -1 : 1;
        }

        return 0;
      }

      if (leftTerm) {
        return -1;
      }

      if (rightTerm) {
        return 1;
      }

      return left.label.localeCompare(right.label);
    });

    return [{ label: "All Terms", value: "all" }, ...sortedItems];
  }, [courseRows, filterOptions?.terms]);
  const sourceItems = useMemo(() => {
    const sourceTypes = filterOptions?.sourceTypes ?? [];

    return [
      { label: "All Sources", value: "all" },
      ...sourceTypes.map((option) => ({ label: option.label, value: option.value })),
    ];
  }, [filterOptions?.sourceTypes]);
  const statusFilterItems = useMemo(
    () => MODERNIZER_STATUS_FILTER_OPTIONS.map((opt) => ({ key: opt.value, value: opt.label })),
    []
  );
  const handleStatusFilterChange = useCallback((values: string[]) => {
    const typedValues = values as ExternalCourseModernizationStatus[];
    setStatusFilters(typedValues);
    setActiveFilter(getActiveFilterForStatusFilters(typedValues));
    setPage(1);
  }, []);
  const uploadTermOptions = useMemo(
    () => [
      { label: "None", value: "none" },
      ...termOptions.map((option) => ({ label: option.label, value: option.value })),
    ],
    [termOptions]
  );

  const persistExternalCourseTerm = async (courseId: string, term: string | null): Promise<string | null> => {
    const selectedOption = term
      ? (termOptions.find((option) => option.value === term) ?? getCourseTermOptionFromLabel(term))
      : null;
    if (term && !selectedOption) {
      return term;
    }

    const result = await setExternalCourseTerm({
      variables: {
        input: {
          externalCourseId: courseId,
          term: selectedOption
            ? {
                season: toAcademicTermSeason(selectedOption.season),
                year: selectedOption.year,
              }
            : null,
        },
      },
      refetchQueries: [{ query: ListExternalCoursesDocument, variables }],
      awaitRefetchQueries: true,
    });
    const payload = result.data?.setExternalCourseTerm;

    if (!payload?.success) {
      throw new Error(payload?.message || "Failed to set course term");
    }

    return payload.data?.term?.label ?? term;
  };

  useEffect(() => {
    if (termFilter === "all") {
      return;
    }

    const hasSelectedTerm = termItems.some((item) => item.value === termFilter);
    if (!hasSelectedTerm) {
      setTermFilter("all");
      setPage(1);
    }
  }, [termFilter, termItems]);

  const updateCourseTerm = (courseId: string, term: string | null) => {
    const previousTermOverride = courseTermsByCourseId[courseId];
    const previousTermLabel =
      previousTermOverride !== undefined
        ? previousTermOverride
        : (courses.find((course) => course.id === courseId)?.term?.label ?? null);

    setCourseTermsByCourseId((previousTerms) => ({
      ...previousTerms,
      [courseId]: term,
    }));
    setCourseTermDropdownCourseId(null);

    void persistExternalCourseTerm(courseId, term)
      .then((savedTermLabel) => {
        setCourseTermsByCourseId((previousTerms) => ({
          ...previousTerms,
          [courseId]: savedTermLabel,
        }));
      })
      .catch((error) => {
        setCourseTermsByCourseId((previousTerms) => ({
          ...previousTerms,
          [courseId]: previousTermLabel,
        }));
        const message = getErrorMessage(error);
        Sentry.captureException(error);
        showToast({ message, type: "error" });
      });
  };

  const handleResetModernizerIntake = async (row: CourseModernizerCourseRow) => {
    const modernizerIntakeId = row.modernizerIntakeId;
    if (!modernizerIntakeId || resettingModernizerIntakeId) {
      return;
    }

    setResettingModernizerIntakeId(modernizerIntakeId);
    try {
      const result = await resetCourseModernizerIntake({
        variables: {
          input: {
            modernizerIntakeId,
          },
        },
        refetchQueries: [{ query: ListExternalCoursesDocument, variables }],
        awaitRefetchQueries: true,
      });
      const payload = result.data?.resetCourseModernizerIntake;

      if (!payload?.success) {
        throw new Error(payload?.message || "Failed to reset course modernizer intake");
      }

      if (activeExternalCourseId === row.course.id) {
        setActiveModernizeJobId(null);
        setActiveExternalCourseId(null);
        setEvaluationModalVisible(false);
      }

      showToast({ message: "Course modernizer reset", type: "success" });
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    } finally {
      setResettingModernizerIntakeId(null);
    }
  };

  const handleCourseTermChange = (courseId: string, term: string) => {
    updateCourseTerm(courseId, term);
  };

  const handleCourseTermClear = (courseId: string) => {
    updateCourseTerm(courseId, null);
  };

  const openCourseModernizerWizard = (row: CourseModernizerCourseRow) => {
    navigation.navigate("Course Modernizer Intake", {
      externalCourseId: row.course.id,
      intakeId: row.modernizerIntakeId ?? undefined,
    });
  };

  const handleModernizerRowAction = (row: CourseModernizerCourseRow, actionId: CourseModernizerRowActionId) => {
    switch (actionId) {
      case "start":
      case "editShell":
        void openModernizerSetupDrawer(row.course);
        return;
      case "openWizard":
        openCourseModernizerWizard(row);
        return;
      case "evaluate":
      case "rerun":
        void handleStartEvaluation(row);
        return;
      case "reset":
        void handleResetModernizerIntake(row);
        return;
      case "viewResults":
        if (row.activeModernizedCourseId) {
          void fetchModernizedCourse({ variables: { courseId: row.activeModernizedCourseId } }).then(() => {
            navigation.navigate("Course Modernizer Review", { courseId: row.activeModernizedCourseId! });
          });
        }
        return;
      default:
        return;
    }
  };

  const openModernizerSetupDrawer = async (course: ExternalCourse) => {
    if (evaluationDisabledReason) {
      showToast({ message: evaluationDisabledReason, type: "warning" });
      return;
    }

    if (courseWithActiveModernization && course.id !== courseWithActiveModernization.id) {
      showToast({
        message: "Another course modernization is already in progress. Please wait until it finishes.",
        type: "warning",
      });
      return;
    }

    const selectedTerm = getCourseTermLabel(course, courseTermsByCourseId[course.id]);
    const defaultArchitectId = getDefaultSetupArchitectId({
      externalCourse: course,
      intake: null,
      currentUserId: userInfo.userId,
      lmsDirectFacultyMode: appConfig.lmsDirectFacultyMode,
      userRole,
    });

    setSelectedCourseForSetup(course);
    resetSetupUserSearch();
    setSetupPinnedUserOptions([]);
    resetSetupForm(buildSetupFormFromShell(course, null, selectedTerm, defaultArchitectId));
    setIsSetupDrawerOpen(true);
    setIsStartingSetup(true);

    try {
      const persistedTermLabel = course.term?.label ?? null;
      const hasTermOverride = courseTermsByCourseId[course.id] !== undefined;

      if ((hasTermOverride || selectedTerm) && selectedTerm !== persistedTermLabel) {
        const savedTermLabel = await persistExternalCourseTerm(course.id, selectedTerm);
        setCourseTermsByCourseId((previousTerms) => ({
          ...previousTerms,
          [course.id]: savedTermLabel,
        }));
      }

      const existingIntakeSummary = course.modernizerIntakeSummary ?? null;
      if (existingIntakeSummary) {
        const result = await getCourseModernizerIntake({
          variables: {
            input: {
              sourceType: CourseModernizerSourceType.Lms,
              sourceId: course.id,
            },
          },
          fetchPolicy: "network-only",
        });
        const payload = result.data?.getCourseModernizerIntake;
        const intake = payload?.data ?? null;
        if (!payload?.success || !intake) {
          throw new Error(payload?.message || "Failed to load modernization setup");
        }
        resetSetupForm(
          buildSetupFormFromShell(
            course,
            intake,
            selectedTerm,
            getDefaultSetupArchitectId({
              externalCourse: course,
              intake,
              currentUserId: userInfo.userId,
              lmsDirectFacultyMode: appConfig.lmsDirectFacultyMode,
              userRole,
            })
          )
        );
        setSetupPinnedUserOptions(getPinnedSetupUserOptions(intake));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
      setIsSetupDrawerOpen(false);
      setSelectedCourseForSetup(null);
      setSetupPinnedUserOptions([]);
      resetSetupUserSearch();
    } finally {
      setIsStartingSetup(false);
    }
  };

  const closeSetupDrawer = () => {
    if (savingCourseModernizerSetup || isStartingSetup) {
      return;
    }

    setIsSetupDrawerOpen(false);
    setSelectedCourseForSetup(null);
    setSetupPinnedUserOptions([]);
    resetSetupForm(DEFAULT_SETUP_FORM);
    resetSetupUserSearch();
  };

  const handleSaveSetup = handleSubmitSetupForm(async (currentSetupForm) => {
    const currentValidationMessage = getSetupValidationMessage(currentSetupForm, activeRubric);
    if (!selectedCourseForSetup || currentValidationMessage || !activeRubric?.id) {
      if (currentValidationMessage) {
        showToast({ message: currentValidationMessage, type: "warning" });
      }
      return;
    }

    const normalizedEducationLevel = currentSetupForm.educationLevel;
    if (!normalizedEducationLevel) {
      showToast({ message: "Education level is required.", type: "warning" });
      return;
    }

    const existingStatus = selectedCourseForSetup.modernizerIntakeSummary?.intakeStatus;
    const hasModernizationOutput =
      existingStatus === CourseModernizerIntakeStatus.ReadyToEvaluate ||
      existingStatus === CourseModernizerIntakeStatus.Evaluating ||
      existingStatus === CourseModernizerIntakeStatus.ReadyForReview ||
      existingStatus === CourseModernizerIntakeStatus.Published;

    if (hasModernizationOutput) {
      const confirmed = window.confirm(
        "This course has already been evaluated. Re-saving setup will archive the existing modernization results and the course will need to be re-evaluated. Do you want to continue?"
      );
      if (!confirmed) return;
    }

    try {
      const selectedTerm = getCourseTermLabel(selectedCourseForSetup, courseTermsByCourseId[selectedCourseForSetup.id]);
      const result = await saveCourseModernizerSetup({
        variables: {
          input: {
            intakeId: currentSetupForm.intakeId || null,
            sourceType: CourseModernizerSourceType.Lms,
            sourceId: selectedCourseForSetup.id,
            sourceUrl: selectedCourseForSetup.url ?? null,
            sourceProvenance: {
              providerCourseId: selectedCourseForSetup.providerCourseId,
              courseCode: selectedCourseForSetup.courseCode,
              term: currentSetupForm.term.trim(),
            },
            name: currentSetupForm.name.trim(),
            courseCode: currentSetupForm.courseCode.trim(),
            architectId: currentSetupForm.architectId,
            editingTeam: currentSetupForm.editingTeam,
            term: currentSetupForm.term.trim() || null,
            educationLevel: normalizedEducationLevel,
            yearLevel: currentSetupForm.yearLevel.trim() || null,
            credits: Number(currentSetupForm.credits),
            lengthInWeeks: Number(currentSetupForm.lengthInWeeks),
            modality: currentSetupForm.modality,
            qualityFrameworkId: activeRubric.id,
          },
        },
        refetchQueries: [{ query: ListExternalCoursesDocument, variables }],
        awaitRefetchQueries: true,
      });

      const payload = result.data?.saveCourseModernizerSetup;
      if (!payload?.success) {
        throw new Error(payload?.message || "Failed to save modernization setup");
      }

      const savedTerm = currentSetupForm.term.trim();
      if (savedTerm && selectedCourseForSetup.id) {
        try {
          await persistExternalCourseTerm(selectedCourseForSetup.id, savedTerm);
        } catch (termError) {
          Sentry.captureException(termError);
          showToast({
            message: "Setup saved but failed to persist term selection. You can set the term from the courses table.",
            type: "warning",
          });
        }
      }

      if (payload.data?.status === CourseModernizerIntakeStatus.Invited) {
        showToast({
          message: hasModernizationOutput
            ? "Previous results archived. Architect invitation queued."
            : "Architect invitation queued",
          type: "success",
        });
      } else if (payload.data?.status === CourseModernizerIntakeStatus.IntakeReady) {
        showToast({
          message: hasModernizationOutput ? "Previous results archived. Intake reset and ready." : "Intake ready",
          type: "success",
        });
      } else {
        showToast({ message: "Modernization setup saved", type: "success" });
      }
      setIsSetupDrawerOpen(false);
      setSelectedCourseForSetup(null);
      setSetupPinnedUserOptions([]);
      resetSetupUserSearch();
    } catch (error) {
      const message = getErrorMessage(error);
      Sentry.captureException(error);
      showToast({ message, type: "error" });
    }
  });

  const rubricBadgeTone: RubricBadgeTone =
    rubricStatus === RubricParseStatus.Ready
      ? "success"
      : rubricStatus === RubricParseStatus.Failed
        ? "error"
        : isRubricProcessing
          ? "processing"
          : "warning";
  const rubricBadgePrimaryText =
    rubricStatus === RubricParseStatus.Ready
      ? (displayedRubric?.name ?? "Rubric configured")
      : rubricStatus === RubricParseStatus.Failed
        ? "Rubric needs attention"
        : isRubricProcessing
          ? "Rubric processing"
          : "No rubric configured";
  const rubricBadgeSecondaryText =
    rubricStatus === RubricParseStatus.Ready
      ? (displayedRubric?.detectedFrameworkType ?? "Active")
      : rubricStatus === RubricParseStatus.Failed
        ? canManageRubric
          ? "Review"
          : null
        : isRubricProcessing
          ? "Working"
          : canManageRubric
            ? "Set up"
            : null;
  const rubricUploadState = rubricCreateInProgress
    ? "creating"
    : rubricUploadInProgress
      ? "uploading"
      : selectedRubricFile
        ? "uploaded"
        : "idle";
  const guidelineUploadState = guidelineCreateInProgress
    ? "creating"
    : guidelineUploadInProgress
      ? "uploading"
      : selectedGuidelineFile
        ? "uploaded"
        : "idle";
  const setupSelectedCourseTerm = selectedCourseForSetup
    ? getCourseTermLabel(selectedCourseForSetup, courseTermsByCourseId[selectedCourseForSetup.id])
    : null;
  const setupUserOptions = useMemo<CourseModernizerSetupUserOption[]>(() => {
    const options = new Map<string, CourseModernizerSetupUserOption>();
    const normalizedSearchQuery = activeSetupUserSearchQuery.trim().toLowerCase();
    const isFacultyOrAbove = (roleName?: string | null) =>
      roleName ? hasMinRole(roleName as UserRoleUI, UserRoleUI.FACULTY) : false;

    setupPinnedUserOptions.forEach((option) => {
      options.set(option.id, option);
    });

    const addUserOption = (user: CourseModernizerSetupUser) => {
      const option = toSetupUserOption(user);
      if (!option) {
        return;
      }

      const searchableText = `${option.label} ${option.subtitle ?? ""}`.toLowerCase();

      if (normalizedSearchQuery && !searchableText.includes(normalizedSearchQuery)) {
        return;
      }

      options.set(option.id, option);
    };

    (setupUsersData?.getUsersWithElevatedRoles?.data?.faculties ?? [])
      .filter((user) => user?.hasRoleRoles?.some((role) => isFacultyOrAbove(role?.name)))
      .forEach(addUserOption);

    return Array.from(options.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [activeSetupUserSearchQuery, setupPinnedUserOptions, setupUsersData?.getUsersWithElevatedRoles?.data?.faculties]);

  const updateSetupForm = useCallback(
    (nextForm: CourseModernizerSetupFormState) => {
      const selectedUserIds = new Set(
        [nextForm.architectId, ...nextForm.editingTeam.map((member) => member.userId)].filter(Boolean)
      );

      setSetupPinnedUserOptions((currentOptions) => {
        const pinnedOptions = new Map<string, CourseModernizerSetupUserOption>();
        currentOptions.forEach((option) => {
          if (selectedUserIds.has(option.id)) {
            pinnedOptions.set(option.id, option);
          }
        });
        setupUserOptions.forEach((option) => {
          if (selectedUserIds.has(option.id)) {
            pinnedOptions.set(option.id, option);
          }
        });
        return Array.from(pinnedOptions.values());
      });
      resetSetupForm(nextForm);
    },
    [resetSetupForm, setupUserOptions]
  );

  const setupValidationMessage = useMemo(
    () => getSetupValidationMessage(setupForm, activeRubric),
    [activeRubric, setupForm]
  );

  return (
    <AdminLayout withPadding={false} isCourseModernizerPage={true}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <PageHeader
            rubricBadgePrimaryText={rubricBadgePrimaryText}
            rubricBadgeSecondaryText={rubricBadgeSecondaryText}
            rubricBadgeTone={rubricBadgeTone}
            canManageRubric={canManageRubric}
            onRubricBadgePress={() => {
              if (canManageRubric) {
                setRubricSheetOpen(true);
              }
            }}
            onUploadDocumentPress={handleOpenUploadDocumentModal}
          />
          <CustomSpacing type="vertical" size="l" />

          <View style={styles.infoCardsContainer}>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<FileMagnifyingGlassIcon />}
                number={summary.totalCourses}
                title="Total Courses"
                iconColor="rgb(37, 99, 235)"
                activeBorderColor="#7EA6FF"
                isActive={activeFilter === "all"}
                onPress={() => {
                  setActiveFilter("all");
                  setStatusFilters([]);
                  setPage(1);
                }}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<CheckCircleIcon />}
                number={summary.modernized}
                title="Modernized"
                iconColor="rgb(22, 163, 74)"
                activeBackgroundColor="#F4FDEB"
                activeBorderColor="#C7E9A0"
                isActive={activeFilter === "modernized"}
                onPress={() => {
                  setActiveFilter("modernized");
                  setStatusFilters(getStatusFiltersForActiveFilter("modernized"));
                  setPage(1);
                }}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<ClockIcon />}
                number={summary.inProgress}
                title="In Progress"
                iconColor="rgb(217, 119, 6)"
                activeBackgroundColor="#FFF8E8"
                activeBorderColor="#F4DA9A"
                isActive={activeFilter === "inProgress"}
                onPress={() => {
                  setActiveFilter("inProgress");
                  setStatusFilters(getStatusFiltersForActiveFilter("inProgress"));
                  setPage(1);
                }}
              />
            </View>
            <View style={styles.infoCardWrapper}>
              <CourseInfoCard
                icon={<CircleIcon />}
                number={summary.notStarted}
                title="Not Started"
                iconColor="rgb(75, 85, 99)"
                activeBorderColor="#7EA6FF"
                isActive={activeFilter === "notStarted"}
                onPress={() => {
                  setActiveFilter("notStarted");
                  setStatusFilters(getStatusFiltersForActiveFilter("notStarted"));
                  setPage(1);
                }}
              />
            </View>
          </View>

          <CustomSpacing type="vertical" size="m" />

          {externalCoursesMessage && courses.length === 0 ? (
            <>
              <CustomSpacing type="vertical" size="s" />
              <CustomText
                text={externalCoursesMessage}
                size="s"
                style={styles.activeModernizationNotice}
                useTranslationText={false}
              />
            </>
          ) : null}
          {showActiveModernizationBanner ? (
            <>
              <View style={styles.activeModernizationBanner}>
                <CustomText
                  text={
                    isCancellingEvaluation
                      ? `Cancelling evaluation for ${activeExternalCourseName || "course"}...`
                      : `Evaluating ${activeExternalCourseName || "course"} (${normalizedModernizePercent}%). Starting another modernization is disabled until it finishes.`
                  }
                  size="s"
                  style={styles.activeModernizationNotice}
                  useTranslationText={false}
                />
                {!isCancellingEvaluation && (
                  <CustomButton
                    text="Cancel"
                    styleType="lightStroke"
                    size="s"
                    useTranslationText={false}
                    onPress={handleCancelEvaluation}
                    style={styles.bannerCancelButton}
                    textStyle={styles.bannerCancelButtonText}
                  />
                )}
              </View>
              <CustomSpacing type="vertical" size="m" />
            </>
          ) : null}

          <CourseModernizerCoursesTable
            isAdmin={isAdmin}
            loading={listExternalCoursesInitialLoading}
            visibleCourses={visibleCourses}
            totalItems={totalItems}
            page={page}
            pageSize={pageSize}
            facultyFilter={facultyFilter}
            facultyFilterDropdownOpen={facultyFilterDropdownOpen}
            termFilter={termFilter}
            termFilterDropdownOpen={termFilterDropdownOpen}
            sourceFilter={sourceFilter}
            sourceFilterDropdownOpen={sourceFilterDropdownOpen}
            statusFilters={statusFilters}
            statusFilterItems={statusFilterItems}
            searchQuery={searchQuery}
            facultyOptions={facultyOptions}
            termItems={termItems}
            sourceItems={sourceItems}
            termOptions={termOptions}
            evaluationDisabledReason={evaluationDisabledReason}
            courseTermDropdownCourseId={courseTermDropdownCourseId}
            backgroundEvaluationCourseId={
              !evaluationModalVisible && isEvaluationWorking ? (activeExternalCourseId ?? null) : null
            }
            backgroundEvaluationPercent={normalizedModernizePercent}
            isCancellingBackgroundEvaluation={isCancellingEvaluation}
            onCancelBackgroundEvaluation={handleCancelEvaluation}
            onFacultyFilterChange={(value) => {
              setFacultyFilter(value);
              setPage(1);
            }}
            setFacultyFilterDropdownOpen={setFacultyFilterDropdownOpen}
            onTermFilterChange={(value) => {
              setTermFilter(value);
              setPage(1);
            }}
            setTermFilterDropdownOpen={setTermFilterDropdownOpen}
            onSourceFilterChange={(value) => {
              setSourceFilter(value as CourseSourceFilter);
              setPage(1);
            }}
            setSourceFilterDropdownOpen={setSourceFilterDropdownOpen}
            onStatusFilterChange={handleStatusFilterChange}
            onCourseTermDropdownChange={setCourseTermDropdownCourseId}
            onCourseTermChange={handleCourseTermChange}
            onCourseTermClear={handleCourseTermClear}
            onSearchQueryChange={(value) => {
              setSearchQuery(value);
              setPage(1);
            }}
            onRowAction={handleModernizerRowAction}
            onViewResults={async (courseId) => {
              await fetchModernizedCourse({ variables: { courseId } });
              navigation.navigate("Course Modernizer Review", { courseId });
            }}
            onPageChange={setPage}
            onItemsPerPageChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
          />
        </View>
        {canManageRubric ? (
          <RubricConfigSlideIn
            visible={rubricSheetOpen}
            onClose={() => setRubricSheetOpen(false)}
            rubric={displayedRubric}
            availableRubrics={availableRubrics}
            loadingRubric={
              activeRubricLoading ||
              (parsedRubricLoading && waitingForReadyRubric && parsedRubric == null) ||
              (parsingRubricLoading && displayedRubric == null)
            }
            loadingAvailableRubrics={availableRubricsLoading}
            savingRubric={savingRubric}
            activatingRubricId={activatingRubricId}
            selectedFileName={selectedRubricFile?.name ?? null}
            selectedFileSize={selectedRubricFile?.size ?? null}
            uploadState={rubricUploadState}
            uploadError={rubricUploadError}
            allowedMimeTypes={allowedMimeTypes}
            maxFileSize={maxFileSize}
            onSelectFile={handleSelectRubricFile}
            onClearFile={() => {
              setSelectedRubricFile(null);
              setRubricUploadError(null);
            }}
            onParseRubric={handleParseRubric}
            onUpdateRubric={handleUpdateRubric}
            onActivateRubric={handleActivateRubric}
            showParsedRubricPreview={waitingForReadyRubric && parsedRubric != null}
            guidelines={guidelines}
            loadingGuidelines={guidelinesLoading}
            selectedGuidelineFileName={selectedGuidelineFile?.name ?? null}
            selectedGuidelineFileSize={selectedGuidelineFile?.size ?? null}
            guidelineUploadState={guidelineUploadState}
            guidelineUploadError={guidelineUploadError}
            deletingGuidelineId={deletingGuidelineId}
            onSelectGuidelineFile={handleSelectGuidelineFile}
            onClearGuidelineFile={() => {
              setSelectedGuidelineFile(null);
              setGuidelineUploadError(null);
            }}
            onCreateGuideline={handleCreateGuideline}
            onDeleteGuideline={handleDeleteGuideline}
          />
        ) : null}
        <CourseModernizeProgressModal
          visible={evaluationModalVisible}
          onRequestClose={handleCloseEvaluationModal}
          isModernizationTerminal={isGenerationTerminal}
          isEvaluationWorking={isEvaluationWorking}
          isModernizationCompleted={isGenerationCompleted}
          isModernizationFailed={isGenerationFailed}
          statusMessage={modernizeStatusPayload?.message}
          currentStepIndex={modernizeCurrentStepIndex}
          totalSteps={modernizeTotalSteps}
          normalizedPercent={normalizedModernizePercent}
          loading={modernizeJobStatusLoading}
          steps={modernizeSteps}
          isCancellingEvaluation={isCancellingEvaluation}
          onCancelEvaluation={() => void handleCancelEvaluation()}
          onContinueToResults={handleContinueToResults}
        />
        <CourseUploadDocumentModal
          visible={uploadDocumentModalOpen}
          onClose={handleCloseUploadDocumentModal}
          selectedFileName={selectedCourseDocument?.localUploadedFile.name ?? null}
          selectedFileSize={selectedCourseDocument?.localUploadedFile.size ?? selectedCourseDocument?.size ?? null}
          courseName={uploadedCourseName}
          courseCode={uploadedCourseCode}
          term={uploadedCourseTerm}
          termOptions={uploadTermOptions}
          uploadInProgress={courseDocumentUploadInProgress}
          submitInProgress={courseDocumentSubmitInProgress}
          ctaDisabled={uploadDocumentCtaDisabled}
          ctaDisabledReason={uploadDocumentCtaDisabledReason}
          allowedMimeTypes={courseDocumentAllowedMimeTypes}
          maxFileSize={courseDocumentMaxFileSize}
          onSelectFile={handleSelectCourseDocument}
          onClearFile={() => {
            setSelectedCourseDocument(null);
            setUploadedCourseName("");
            setUploadedCourseCode("");
            setCourseDocumentUploadError(null);
          }}
          onCourseNameChange={setUploadedCourseName}
          onCourseCodeChange={setUploadedCourseCode}
          onTermChange={setUploadedCourseTerm}
          onSubmit={handleUploadDocumentEvaluate}
        />
      </View>

      <CourseModernizerSetupDrawer
        visible={isSetupDrawerOpen}
        courseSummary={
          selectedCourseForSetup
            ? {
                name: selectedCourseForSetup.name || "Untitled course",
                code: getModernizerCourseCode(selectedCourseForSetup) || null,
                term: setupSelectedCourseTerm,
                faculty: getTeachingFacultySummary(selectedCourseForSetup),
              }
            : null
        }
        form={setupForm}
        onFormChange={updateSetupForm}
        userOptions={setupUserOptions}
        architectSearchQuery={setupArchitectSearchQuery}
        editingTeamSearchQuery={setupEditingTeamSearchQuery}
        onArchitectSearchQueryChange={(value) => {
          setSetupUserSearchField("architect");
          setSetupArchitectSearchQuery(value);
        }}
        onEditingTeamSearchQueryChange={(value) => {
          setSetupUserSearchField("editingTeam");
          setSetupEditingTeamSearchQuery(value);
        }}
        onArchitectSearchFocus={() => setSetupUserSearchField("architect")}
        onEditingTeamSearchFocus={() => setSetupUserSearchField("editingTeam")}
        currentUserId={userInfo.userId}
        loadingShell={isStartingSetup}
        saving={savingCourseModernizerSetup}
        saveDisabled={Boolean(setupValidationMessage) || isStartingSetup}
        validationMessage={setupValidationMessage}
        onClose={closeSetupDrawer}
        onSave={() => void handleSaveSetup()}
      />
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
  infoCardsContainer: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  infoCardWrapper: {
    flex: 1,
    minWidth: 220,
  },
  activeModernizationNotice: {
    color: "#B54708",
  },
  activeModernizationBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  bannerCancelButton: {
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  bannerCancelButtonText: {
    color: "#B42318",
    fontSize: 13,
    fontWeight: "600",
  },
  headerCard: {
    paddingVertical: 6,
    paddingHorizontal: 0,
    width: "100%",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  headerTitle: {
    color: "#101828",
    fontSize: 24,
    lineHeight: 32,
  },
  uploadDocumentButton: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  uploadDocumentButtonText: {
    color: "#475467",
    fontSize: 13,
    fontWeight: "500",
  },
  rubricBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 12,
    gap: 8,
  },
  warningRubricBadge: {
    backgroundColor: "#FFFAEB",
    borderColor: "#FEDF89",
  },
  configuredRubricBadge: {
    backgroundColor: "#F5F8FF",
    borderColor: "#D1E0FF",
  },
  processingRubricBadge: {
    backgroundColor: "#FFFAEB",
    borderColor: "#FEDF89",
  },
  errorRubricBadge: {
    backgroundColor: "#FEF3F2",
    borderColor: "#FECDCA",
  },
  rubricBadgePrimaryText: {
    lineHeight: 20,
  },
  rubricBadgeSecondaryText: {
    lineHeight: 18,
  },
  warningRubricText: {
    color: "#B54708",
  },
  configuredRubricPrimaryText: {
    color: "#175CD3",
  },
  configuredRubricSecondaryText: {
    color: "#3E68FE",
  },
  processingRubricText: {
    color: "#B54708",
  },
  errorRubricText: {
    color: "#B42318",
  },
});

export default CourseModernizerView;
